/**
 * Cycle Results Page — /prioritization/cycles/:cycleId/results
 *
 * What the cycle has decided so far, read from the durable tiers (canon 03
 * §2.9, canon 08 §3) and never from the in-memory session list:
 *
 *   - each track's published result   (`track_results`, migration 934 — the
 *     person who holds the ball publishes it for the track)
 *   - what went up through Catchball   (`loop_results`, tier 2)
 *   - the cycle report                 (`cycle_reports`, tier 3)
 *   - the governance trail             (`governance_events`)
 *
 * Until 2026-10-15 this page listed the cycle's sessions from memory and
 * called that the cycle's results; a session is the method's output, not the
 * cycle's answer.
 */

import { useMemo, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronLeftIcon,
  TrophyIcon,
  ClockIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useCycleReportStore } from '../../../stores/prioritization/cycleReportStore';
import { usePrioritySetStore } from '../../../stores/prioritization/prioritySetStore';
import { useItemPoolsStore } from '../../../stores/prioritization/itemPoolsStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useCatchballStore, type LoopResult } from '../../../stores/prioritization/catchballStore';
import { getSafeSupabaseClient, isSupabaseAvailable } from '../../../lib/supabaseSync';
import { CHIP_OVERFLOW_CLASS, CLASSIFICATION_CHIP_CLASS } from '../../../lib/classificationDisplay';
import {
  formatGovernanceEventNote,
  formatGovernanceEventType,
} from '../../../lib/formatGovernanceEventDisplay';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { useActorDisplayMap } from '../../../hooks/prioritization/useActorDisplayMap';
import { RelationshipSplitsPanel } from '@/components/prioritization/RelationshipSplitsPanel';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import type { RankedResult } from '@/lib/itemRelationshipFlags';
import { DataTable, Panel, EmptyState, type Column } from '@/components/ds';
import { ObjectRef } from '@/components/ds/ObjectRef';
import {
  fetchPublishedTrackResults,
  fetchCurrentTrackResults,
  type PublishedTrackResult,
  type TrackResultRecord,
} from '@/lib/tracks/publishedTrackResult';
import type { Track } from '@/types/prioritization/track';
import { formatDate } from '@/i18n/formatters';

/** Month, day and year — the one date form this page shows. */
const CYCLE_RESULT_DATE: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };

// ============================================================================
// Governance Event Type
// ============================================================================

interface GovernanceEvent {
  id: string;
  event_type: string;
  actor_name?: string;
  occurred_at: string;
  note?: string;
  from_state?: string;
  to_state?: string;
}

const DECISION_WORDS: Record<string, string> = {
  accepted: 'Accepted',
  accepted_provisional: 'Accepted with conditions',
  returned: 'Returned',
  rejected: 'Rejected',
};

interface TrackRow {
  track: Track;
  summary: PublishedTrackResult | undefined;
  record: TrackResultRecord | undefined;
}

export function CycleResults() {
  const { cycleId = '' } = useParams();
  const orgUnits = useEnterpriseIdentityStore((s) => s.orgUnits);
  const resolveOrgUnitName = (id: string) => {
    const unit = orgUnits.get(id);
    return unit?.displayName || unit?.name;
  };

  // Get cycle info
  const cycles = useDegapProcessStore((state) => state.cycles);
  const cycle = useMemo(() => cycles.find((c) => c.id === cycleId), [cycles, cycleId]);

  // ── Tracks and their published results ──────────────────────────────────
  const allTracks = useTrackStore((state) => state.tracks);
  const fetchTracksForCycle = useTrackStore((state) => state.fetchTracksForCycle);
  const tracks = useMemo(
    () =>
      allTracks
        .filter((t) => t.cycleId === cycleId)
        .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime()),
    [allTracks, cycleId],
  );
  const trackIdsKey = tracks.map((t) => t.id).join(',');
  const [published, setPublished] = useState<Map<string, PublishedTrackResult>>(() => new Map());
  const [records, setRecords] = useState<Map<string, TrackResultRecord>>(() => new Map());
  const [resultsLoaded, setResultsLoaded] = useState(false);
  useEffect(() => {
    if (!cycleId) return;
    void fetchTracksForCycle(cycleId);
  }, [cycleId, fetchTracksForCycle]);
  useEffect(() => {
    const ids = trackIdsKey ? trackIdsKey.split(',') : [];
    if (ids.length === 0) {
      setResultsLoaded(true);
      return;
    }
    let cancelled = false;
    void Promise.all([fetchPublishedTrackResults(ids), fetchCurrentTrackResults(ids)]).then(([p, r]) => {
      if (cancelled) return;
      setPublished(p);
      setRecords(r);
      setResultsLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [trackIdsKey]);

  const items = useItemsStore((state) => state.items);
  const fetchItems = useItemsStore((state) => state.fetchFromSupabase);
  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);
  const getItemTitle = (itemId: string) => items.find((c) => c.id === itemId)?.title || 'Unknown Item';
  const itemPools = useItemPoolsStore((state) => state.itemPools);

  const trackRows = useMemo<TrackRow[]>(
    () => tracks.map((track) => ({ track, summary: published.get(track.id), record: records.get(track.id) })),
    [tracks, published, records],
  );
  const tracksWithResult = trackRows.filter((r) => r.record).length;

  // Where the Tracks meet: each Track's published result, compared for linked
  // items whose results disagree (canon 12 Rule 17).
  const meetingResults = useMemo<RankedResult[]>(
    () =>
      trackRows
        .filter((r) => r.record)
        .map((r) => ({
          id: r.track.id,
          name: r.track.name,
          rankedItemIds: r.record!.items.map((i) => i.itemId),
          cutLinePosition: r.record!.cutLinePosition,
        })),
    [trackRows],
  );
  const meetingResultLink = (id: string) => `/prioritization/tracks/${id}/result`;

  // ── Catchball passes (loop results, tier 2) ─────────────────────────────
  const fetchCycleLoopResults = useCatchballStore((s) => s.fetchCycleLoopResults);
  const [loopResults, setLoopResults] = useState<LoopResult[]>([]);
  useEffect(() => {
    if (!cycleId) return;
    let cancelled = false;
    void fetchCycleLoopResults(cycleId).then((rows) => {
      if (!cancelled) setLoopResults(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [cycleId, fetchCycleLoopResults]);
  const actorIds = useMemo(
    () => [...new Set(loopResults.flatMap((r) => [r.submitted_by, r.resolved_by]).filter((x): x is string => Boolean(x)))],
    [loopResults],
  );
  const actorNames = useActorDisplayMap(actorIds);
  const trackName = (id: string) => tracks.find((t) => t.id === id)?.name ?? 'a track';

  // ── Cycle report (tier 3) ───────────────────────────────────────────────
  const allReports = useCycleReportStore((state) => state.reports);
  const cycleReports = useMemo(
    () => allReports.filter((r) => r.cycleId === cycleId).sort((a, b) => b.version - a.version),
    [allReports, cycleId],
  );
  const latestReport = cycleReports[0];
  const publishedReport = useMemo(
    () => allReports.find((r) => r.cycleId === cycleId && r.legitimacyState === 'published'),
    [allReports, cycleId],
  );
  const fetchCycleReports = useCycleReportStore((state) => state.fetchReportsForCycle);

  // ── Priority Lists - for governance status ──────────────────────────────
  const allPrioritySets = usePrioritySetStore((state) => state.prioritySets);
  const prioritySets = useMemo(
    () => allPrioritySets.filter((s) => s.cycleId === (cycleId || '')),
    [allPrioritySets, cycleId],
  );
  const currentPrioritySet = prioritySets.find((s) => s.state !== 'superseded');
  const fetchPrioritySets = usePrioritySetStore((state) => state.loadPrioritySetsForCycle);
  const currentPrioritySetPools = useMemo(() => {
    if (!currentPrioritySet) return [];
    return itemPools.filter((itemPool) => currentPrioritySet.itemPoolIds.includes(itemPool.id));
  }, [currentPrioritySet, itemPools]);
  const priorityContextByItemId = useMemo(() => {
    const context = new Map<string, { tags: string[]; sourceListNames: string[] }>();
    currentPrioritySetPools.forEach((itemPool) => {
      itemPool.priorityIds.forEach((itemId) => {
        const existing = context.get(itemId);
        const tags = itemPool.tags?.filter(Boolean) ?? [];
        if (!existing) {
          context.set(itemId, { tags, sourceListNames: [itemPool.name] });
          return;
        }
        context.set(itemId, {
          tags: Array.from(new Set([...existing.tags, ...tags])),
          sourceListNames: existing.sourceListNames.includes(itemPool.name)
            ? existing.sourceListNames
            : [...existing.sourceListNames, itemPool.name],
        });
      });
    });
    return context;
  }, [currentPrioritySetPools]);

  // ── Governance Events ───────────────────────────────────────────────────
  const [governanceEvents, setGovernanceEvents] = useState<GovernanceEvent[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);

  useEffect(() => {
    if (!cycleId) return;
    fetchCycleReports(cycleId);
    fetchPrioritySets(cycleId);

    async function loadGovernanceEvents() {
      if (!isSupabaseAvailable()) return;
      const supabase = getSafeSupabaseClient();
      if (!supabase) return;
      setIsLoadingEvents(true);
      try {
        const { data, error } = await supabase
          .from('governance_events')
          .select('*')
          .eq('cycle_id', cycleId)
          .order('occurred_at', { ascending: false })
          .limit(20);
        if (!error && data) setGovernanceEvents(data);
      } catch (err) {
        console.error('[CycleResults] Failed to fetch governance events:', err);
      } finally {
        setIsLoadingEvents(false);
      }
    }
    void loadGovernanceEvents();
  }, [cycleId, fetchCycleReports, fetchPrioritySets]);

  if (!cycle) {
    return (
      <div className="p-8 text-center">
        <p className="text-[var(--text-ter)]">Cycle not found</p>
        <Link to="/prioritization/cycles" className="text-[var(--text-ter)] hover:underline mt-2 inline-block">
          Back to Cycles
        </Link>
      </div>
    );
  }

  const trackColumns: Column<TrackRow>[] = [
    {
      key: 'track',
      header: 'Track',
      render: (r) => <ObjectRef kind="track" id={r.track.id} name={r.track.name} hideWord />,
    },
    {
      key: 'result',
      header: 'Published result',
      render: (r) =>
        r.record ? (
          <span className="text-[var(--text-pri)]">
            {r.record.strategy === 'aggregate'
              ? `Combined from ${r.record.sourceSessionIds.length} session${r.record.sourceSessionIds.length === 1 ? '' : 's'}`
              : r.summary?.sessionName ?? 'One session'}
            <span className="text-[var(--text-ter)]"> · {r.record.itemCount} items</span>
            {r.summary && r.summary.waitingCount > 0 && (
              <span className="text-[var(--mustard)]">
                {' '}· {r.summary.waitingCount} newer session{r.summary.waitingCount === 1 ? '' : 's'} wait on the ball holder
              </span>
            )}
          </span>
        ) : (
          <span className="text-[var(--text-ter)]">
            {r.summary ? `${r.summary.reason.charAt(0).toUpperCase()}${r.summary.reason.slice(1)}` : 'Not read yet'}
          </span>
        ),
    },
    {
      key: 'top',
      header: 'Top of the list',
      render: (r) =>
        r.record ? (
          <span className="text-[var(--text-sec)]">
            {r.record.items.slice(0, 3).map((i, idx) => `${idx + 1}. ${getItemTitle(i.itemId)}`).join('  ')}
            {r.record.items.length > 3 ? ` … +${r.record.items.length - 3}` : ''}
          </span>
        ) : (
          ''
        ),
    },
    {
      key: 'by',
      header: 'Published',
      width: '12rem',
      render: (r) =>
        r.record ? (
          <span className="text-[var(--text-sec)]">
            {r.record.publishedByName ?? 'the ball holder'} · {formatDate(r.record.publishedAt ?? '', CYCLE_RESULT_DATE)}
          </span>
        ) : (
          ''
        ),
    },
    {
      key: 'open',
      header: '',
      width: '6rem',
      render: (r) => (
        <Link to={`/prioritization/tracks/${r.track.id}/result`} className="text-xs underline text-[var(--text-ter)] hover:text-[var(--text-pri)]">
          Open
        </Link>
      ),
    },
  ];

  const loopColumns: Column<LoopResult>[] = [
    { key: 'turn', header: 'Pass', width: '4rem', numeric: true, render: (r) => String(r.turn_number) },
    {
      key: 'scope',
      header: 'Tracks',
      render: (r) => (r.scope_track_ids ?? []).map(trackName).join(', ') || '—',
    },
    {
      key: 'sent',
      header: 'Sent up',
      render: (r) => (
        <span className="text-[var(--text-sec)]">
          {actorNames[r.submitted_by] ?? 'Someone'} · {formatDate(r.submitted_at ?? '', CYCLE_RESULT_DATE)}
        </span>
      ),
    },
    {
      key: 'decision',
      header: 'Decision',
      render: (r) =>
        r.evaluator_decision ? (
          <span className="text-[var(--text-pri)]">
            {DECISION_WORDS[r.evaluator_decision] ?? r.evaluator_decision}
            {r.resolved_at ? <span className="text-[var(--text-ter)]"> · {formatDate(r.resolved_at ?? '', CYCLE_RESULT_DATE)}</span> : null}
          </span>
        ) : (
          <span className="text-[var(--text-ter)]">Awaiting a decision</span>
        ),
    },
    {
      key: 'why',
      header: 'Rationale',
      render: (r) => (
        <span className="text-[var(--text-sec)]">
          {(r.rationale ?? '').length > 140 ? `${(r.rationale ?? '').slice(0, 140)}…` : r.rationale ?? ''}
        </span>
      ),
    },
    {
      key: 'open',
      header: '',
      width: '6rem',
      render: (r) =>
        r.proposal_id ? (
          <Link to={`/prioritization/cascade/proposal/${r.proposal_id}`} className="text-xs underline text-[var(--text-ter)] hover:text-[var(--text-pri)]">
            Open
          </Link>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-6">
        <Link
          to={`/prioritization/cycles/${cycleId}`}
          className="inline-flex items-center text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] mb-3"
        >
          <ChevronLeftIcon className="h-4 w-4 mr-1" />
          Back to Cycle
        </Link>
        <PageHeader
          icon={<TrophyIcon className="h-4 w-4 text-[var(--mustard)]" />}
          iconBg="bg-[var(--mustard-dim)]"
          title={`${cycle.name} — Results`}
          subtitle="What each track published, what went up through Catchball, and what the cycle ratified"
          mb="mb-0"
        />
      </div>

      <RelationshipSplitsPanel results={meetingResults} linkFor={meetingResultLink} className="mb-8" />

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-4">
          <p className="text-2xl font-bold text-[var(--text-pri)]">
            {tracksWithResult}
            <span className="text-base font-normal text-[var(--text-ter)]"> of {tracks.length}</span>
          </p>
          <p className="text-sm text-[var(--text-ter)]">Tracks with a published result</p>
        </div>
        <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-4">
          <p className="text-2xl font-bold text-[var(--text-pri)]">{loopResults.length}</p>
          <p className="text-sm text-[var(--text-ter)]">Catchball passes</p>
        </div>
        <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-4">
          <p className="text-2xl font-bold text-[var(--text-pri)]">
            {latestReport
              ? latestReport.legitimacyState.charAt(0).toUpperCase() + latestReport.legitimacyState.slice(1)
              : 'None'}
          </p>
          <p className="text-sm text-[var(--text-ter)]">Cycle report</p>
        </div>
      </div>

      {/* ── What each track published ─────────────────────────────────────── */}
      <Panel label="Tracks" title="What each track published" count={tracks.length} flush>
        {tracks.length === 0 ? (
          <EmptyState
            size="inline"
            title="This cycle has no tracks yet"
            description="A track holds the sessions whose results are published for it."
            action={
              <Link to={`/prioritization/cycles/${cycleId}`} className="text-sm underline text-[var(--text-pri)]">
                Open the cycle
              </Link>
            }
          />
        ) : !resultsLoaded ? (
          <p className="px-4 py-3 text-sm text-[var(--text-ter)]">Reading the tracks' results…</p>
        ) : (
          <DataTable<TrackRow> columns={trackColumns} rows={trackRows} getRowKey={(r) => r.track.id} density="compact" bordered={false} />
        )}
      </Panel>

      {/* ── What went up through Catchball ───────────────────────────────── */}
      <Panel label="Catchball" title="What went up" count={loopResults.length} flush>
        {loopResults.length === 0 ? (
          <EmptyState
            size="inline"
            title="Nothing has gone up yet"
            description="Each pass of the ball — the proposal sent up and the decision on it — is recorded here."
            action={
              <Link to={`/prioritization/cascade/dashboard/${cycleId}`} className="text-sm underline text-[var(--text-pri)]">
                Open Catchball
              </Link>
            }
          />
        ) : (
          <DataTable<LoopResult> columns={loopColumns} rows={loopResults} getRowKey={(r) => r.id} density="compact" bordered={false} />
        )}
      </Panel>

      {/* ── Cycle report (tier 3) ─────────────────────────────────────────── */}
      {latestReport ? (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
            <DocumentTextIcon className="h-5 w-5 text-[var(--text-ter)]" />
            Published Priority List
            <Link to={`/prioritization/cycles/${cycleId}/report`} className="ml-auto text-xs font-normal underline text-[var(--text-ter)] hover:text-[var(--text-pri)]">
              Open the cycle report
            </Link>
          </h2>

          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="px-6 py-4 border-b border-[var(--rule)]">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-[var(--text-pri)]">{latestReport.cycleName || 'Priority Plan'}</h3>
                  <div className="flex items-center gap-4 mt-1 text-sm text-[var(--text-ter)]">
                    <span className="flex items-center gap-1">
                      <ClockIcon className="h-4 w-4" />
                      {new Date(latestReport.createdAt).toLocaleDateString()}
                    </span>
                    <span
                      className={`
                      px-2 py-0.5 rounded text-xs font-medium
                      ${latestReport.legitimacyState === 'published' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : ''}
                      ${latestReport.legitimacyState === 'ratified' ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' : ''}
                      ${latestReport.legitimacyState === 'superseded' ? 'bg-[var(--hover-tint)] text-[var(--text-ter)]' : ''}
                    `}
                    >
                      {latestReport.legitimacyState.charAt(0).toUpperCase() + latestReport.legitimacyState.slice(1)}
                    </span>
                    <span className="text-[var(--text-ter)]">v{latestReport.version}</span>
                  </div>
                </div>

                {publishedReport && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] rounded-full text-sm">
                    <CheckCircleIcon className="h-4 w-4" />
                    Published
                  </span>
                )}
              </div>
            </div>

            {latestReport.planOfRecord?.ranked_items && latestReport.planOfRecord.ranked_items.length > 0 && (
              <div className="p-4">
                <h4 className="text-sm font-medium text-[var(--text-sec)] mb-3">Top Priorities</h4>
                <div className="space-y-2">
                  {latestReport.planOfRecord.ranked_items.slice(0, 5).map((priority, index) => (
                    <div key={priority.item_id} className="flex items-start gap-3 text-sm">
                      <span
                        className={`
                        w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                        ${index < 3 ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'}
                      `}
                      >
                        {priority.rank}
                      </span>
                      <div>
                        <span className="text-[var(--text-pri)]">{priority.title || getItemTitle(priority.item_id)}</span>
                        {priorityContextByItemId.get(priority.item_id)?.tags?.length ? (
                          <div className="mt-1 flex flex-wrap gap-1.5">
                            {priorityContextByItemId
                              .get(priority.item_id)!
                              .tags.slice(0, 3)
                              .map((tag) => (
                                <span key={`${priority.item_id}-${tag}`} className={CLASSIFICATION_CHIP_CLASS}>
                                  {tag}
                                </span>
                              ))}
                            {priorityContextByItemId.get(priority.item_id)!.tags.length > 3 && (
                              <span className={CHIP_OVERFLOW_CLASS} title={priorityContextByItemId.get(priority.item_id)!.tags.join(', ')}>
                                +{priorityContextByItemId.get(priority.item_id)!.tags.length - 3} more
                              </span>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))}
                  {latestReport.planOfRecord.ranked_items.length > 5 && (
                    <p className="text-xs text-[var(--text-ter)] pl-9">+{latestReport.planOfRecord.ranked_items.length - 5} more items</p>
                  )}
                </div>

                {latestReport.planOfRecord.cut_line_position != null && (
                  <div className="mt-4 pt-4 border-t border-[var(--rule)]">
                    <p className="text-sm text-[var(--mustard)]">
                      <span className="font-medium">Cut Line:</span> Position {latestReport.planOfRecord.cut_line_position}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <Panel label="Cycle report" title="Priorities not yet ratified">
          <p className="text-sm text-[var(--text-sec)]">
            The cycle report is written when a proposal is accepted and the priorities are ratified. Until then the
            tracks' published results above are the cycle's answers so far.
          </p>
          <Link to={`/prioritization/cycles/${cycleId}/report`} className="inline-block mt-2 text-sm underline text-[var(--text-pri)]">
            Open the cycle report page
          </Link>
        </Panel>
      )}

      {/* ── Governance Audit Trail ────────────────────────────────────────── */}
      {(governanceEvents.length > 0 || currentPrioritySet) && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
            <ShieldCheckIcon className="h-5 w-5 text-[var(--text-ter)]" />
            Governance Audit Trail
          </h2>

          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            {currentPrioritySet && (
              <div className="px-6 py-4 border-b border-[var(--rule)] bg-[var(--surface)]">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-[var(--text-ter)]">Current State:</span>
                    <span
                      className={`
                      ml-2 px-2 py-1 rounded text-sm font-medium
                      ${currentPrioritySet.state === 'published' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : ''}
                      ${currentPrioritySet.state === 'ratified' ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' : ''}
                      ${currentPrioritySet.state === 'decided' ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' : ''}
                      ${currentPrioritySet.state === 'proposed' ? 'bg-[var(--hover-tint)] text-[var(--text-pri)]' : ''}
                      ${currentPrioritySet.state === 'superseded' ? 'bg-[var(--coral-dim)] text-[var(--coral)]' : ''}
                    `}
                    >
                      {currentPrioritySet.state.charAt(0).toUpperCase() + currentPrioritySet.state.slice(1)}
                    </span>
                  </div>
                  {currentPrioritySet.publication?.publishedAt && (
                    <span className="text-sm text-[var(--text-ter)]">
                      Published: {currentPrioritySet.publication.publishedAt.toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            )}

            {isLoadingEvents ? (
              <div className="p-6 text-center">
                <ArrowPathIcon className="h-6 w-6 text-[var(--text-ter)] animate-spin mx-auto" />
              </div>
            ) : governanceEvents.length > 0 ? (
              <div className="divide-y divide-[var(--rule)]">
                {governanceEvents.map((event) => (
                  <div key={event.id} className="px-6 py-4 flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-[var(--hover-tint)]">
                      <CheckCircleIcon className="h-4 w-4 text-[var(--text-sec)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[var(--text-pri)]">{formatGovernanceEventType(event.event_type)}</span>
                        {event.from_state && event.to_state && (
                          <span className="text-xs text-[var(--text-ter)]">
                            ({event.from_state} → {event.to_state})
                          </span>
                        )}
                      </div>
                      <GovernanceEventNote note={event.note} eventType={event.event_type} resolveOrgUnitName={resolveOrgUnitName} />
                      <div className="flex items-center gap-3 mt-1 text-xs text-[var(--text-ter)]">
                        <span>{new Date(event.occurred_at).toLocaleString()}</span>
                        {event.actor_name && <span>by {event.actor_name}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-[var(--text-ter)]">
                <p>No governance events recorded yet.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function GovernanceEventNote({
  note,
  eventType,
  resolveOrgUnitName,
}: {
  note?: string;
  eventType: string;
  resolveOrgUnitName: (id: string) => string | undefined;
}) {
  const text = formatGovernanceEventNote(note, { eventType, resolveOrgUnitName });
  if (!text) return null;
  return <p className="text-sm text-[var(--text-sec)] mt-0.5">{text}</p>;
}

export default CycleResults;
